import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook para gerenciar Contexto Multi-Camada (Fase 3 Sessão 3).
 *
 * Arquitetura:
 *   Camada 1: ai_context_global     → contexto da Aston, sempre ativo
 *   Camada 2: ai_context_empresa    → notas por empresa, categorizadas
 *   Camada 3: deal_context          → notas por oferta (já existia)
 *
 * Padrão HITL: toda escrita preserva fonte + trilha (updated_at, updated_by).
 * Quando IA infere algo e humano edita, conteudo_original_ia preserva o texto IA.
 */
export const useAIContext = () => {
  const [globalContext, setGlobalContext] = useState([]);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [error, setError] = useState(null);

  const fetchGlobal = useCallback(async () => {
    setLoadingGlobal(true);
    try {
      const { data, error: err } = await supabase
        .from('ai_context_global')
        .select('*')
        .eq('ativo', true)
        .order('ordem', { ascending: true });
      if (err) throw err;
      setGlobalContext(data || []);
    } catch (err) {
      console.error('Erro ao buscar contexto global:', err);
      setError(err.message);
    } finally {
      setLoadingGlobal(false);
    }
  }, []);

  useEffect(() => { fetchGlobal(); }, [fetchGlobal]);

  // === CONTEXTO GLOBAL ===

  const updateGlobal = async (id, patches, userEmail) => {
    if (!id) throw new Error('id é obrigatório');
    const payload = {
      ...patches,
      updated_at: new Date().toISOString(),
      updated_by: userEmail || 'desconhecido',
    };
    const { data, error: err } = await supabase
      .from('ai_context_global')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (err) throw err;
    await fetchGlobal();
    return data;
  };

  const createGlobal = async (payload, userEmail) => {
    const clean = {
      chave: payload.chave?.trim(),
      titulo: payload.titulo?.trim(),
      conteudo: payload.conteudo?.trim(),
      categoria: payload.categoria || 'geral',
      ordem: payload.ordem || 99,
      ativo: payload.ativo !== false,
      updated_by: userEmail || 'desconhecido',
    };
    if (!clean.chave || !clean.titulo || !clean.conteudo) {
      throw new Error('chave, titulo e conteudo são obrigatórios');
    }
    const { data, error: err } = await supabase
      .from('ai_context_global')
      .insert(clean)
      .select()
      .single();
    if (err) throw err;
    await fetchGlobal();
    return data;
  };

  const deleteGlobal = async (id) => {
    const { error: err } = await supabase
      .from('ai_context_global')
      .delete()
      .eq('id', id);
    if (err) throw err;
    await fetchGlobal();
  };

  // === CONTEXTO POR EMPRESA ===

  const fetchEmpresaContext = async (empresaId) => {
    if (!empresaId) return [];
    const { data, error: err } = await supabase
      .from('ai_context_empresa')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('ativo', true)
      .order('created_at', { ascending: false });
    if (err) throw err;
    return data || [];
  };

  const createEmpresaNote = async (empresaId, payload, userEmail) => {
    if (!empresaId) throw new Error('empresa_id é obrigatório');
    const clean = {
      empresa_id: empresaId,
      categoria: payload.categoria || 'observacao',
      conteudo: payload.conteudo?.trim(),
      fonte: payload.fonte || 'manual',
      created_by: userEmail || 'desconhecido',
      updated_by: userEmail || 'desconhecido',
    };
    if (!clean.conteudo || clean.conteudo.length < 3) {
      throw new Error('conteudo precisa ter pelo menos 3 caracteres');
    }
    const { data, error: err } = await supabase
      .from('ai_context_empresa')
      .insert(clean)
      .select()
      .single();
    if (err) throw err;
    return data;
  };

  const updateEmpresaNote = async (id, patches, userEmail, existingNote) => {
    if (!id) throw new Error('id é obrigatório');

    // HITL: se nota atual veio de IA e usuário está editando, preservar original
    const payload = {
      ...patches,
      updated_at: new Date().toISOString(),
      updated_by: userEmail || 'desconhecido',
    };

    if (
      patches.conteudo &&
      existingNote?.fonte === 'ai_inferido' &&
      !existingNote?.conteudo_original_ia
    ) {
      payload.conteudo_original_ia = existingNote.conteudo;
      payload.fonte = 'ai_editado';
    }

    const { data, error: err } = await supabase
      .from('ai_context_empresa')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (err) throw err;
    return data;
  };

  const deleteEmpresaNote = async (id) => {
    // Soft delete: desativa em vez de apagar (preserva histórico)
    const { error: err } = await supabase
      .from('ai_context_empresa')
      .update({ ativo: false, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) throw err;
  };

  return {
    // Global
    globalContext,
    loadingGlobal,
    refetchGlobal: fetchGlobal,
    createGlobal,
    updateGlobal,
    deleteGlobal,
    // Empresa
    fetchEmpresaContext,
    createEmpresaNote,
    updateEmpresaNote,
    deleteEmpresaNote,
    // Estado geral
    error,
  };
};
